import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import jwt from "jwt-decode";

import user_service from "../service/user_service";
import Loader from "../../Pages/Loader/Loader.js";
import Toaster from "../../Pages/Toaster/Toaster";
import RecipientPicker from "../common/RecipientPicker";

import { Editor } from "react-draft-wysiwyg";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

function toLocalDateInputValue(d) {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function toLocalDateTimeInputValue(d) {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/** Parse datetime-local value as local wall-clock time (avoids UTC offset bugs). */
function fromLocalDateTimeInputValue(v) {
  if (!v) return null;
  const [datePart, timePart = ""] = v.split("T");
  const [y, m, day] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  const date = new Date(y, m - 1, day, hh, mm || 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export default function NewsletterAdminEditor({ readOnly = false }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loader, setLoader] = useState({ isActive: null });
  const { isActive } = loader;

  const [toaster, setToaster] = useState({
    types: null,
    isShow: null,
    toasterBody: "",
    message: "",
  });
  const { types, isShow, toasterBody, message } = toaster;

  const isAdmin = useMemo(() => {
    try {
      return String(jwt(localStorage.getItem("auth"))?.contactType || "").toLowerCase() === "admin";
    } catch {
      return false;
    }
  }, []);

  const [newsletter, setNewsletter] = useState(null);
  const [title, setTitle] = useState("");
  const [publishDate, setPublishDate] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [status, setStatus] = useState("draft");
  const [visibleInArchive, setVisibleInArchive] = useState(true);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [contactsAssociate, setContactsAssociate] = useState([]);
  const [contactsGroups, setContactsGroups] = useState([]);
  const [officeGroups, setOfficeGroups] = useState([]);
  const [includeBirthdaysAnniversaries, setIncludeBirthdaysAnniversaries] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoader({ isActive: true });
    try {
      const res = await user_service.newslettersGetById(id);
      const n = res?.data;
      setNewsletter(n);
      setTitle(n?.title || "");
      setPublishDate(toLocalDateInputValue(n?.publishDate));
      setSendAt(toLocalDateTimeInputValue(n?.sendAt));
      setStatus(n?.status || "draft");
      setVisibleInArchive(Boolean(n?.visibleInArchive));
      setContactsAssociate(Array.isArray(n?.contactType) ? n.contactType : []);
      setContactsGroups(Array.isArray(n?.contactGroup) ? n.contactGroup : []);
      setOfficeGroups(Array.isArray(n?.officeGroup) ? n.officeGroup : []);
      setIncludeBirthdaysAnniversaries(Boolean(n?.includeBirthdaysAnniversaries));
      const html = n?.body || "";
      if (html) {
        const contentBlock = htmlToDraft(html);
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setEditorState(EditorState.createWithContent(contentState));
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } catch (e) {
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: e?.response?.data?.message || "Failed to load newsletter.",
        message: "Error",
      });
    } finally {
      setLoader({ isActive: false });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const bodyHtml = useMemo(
    () => draftToHtml(convertToRaw(editorState.getCurrentContent())),
    [editorState]
  );

  const save = async (nextStatus) => {
    setLoader({ isActive: true });
    try {
      const payload = {
        title,
        body: bodyHtml,
        publishDate: publishDate ? new Date(publishDate).toISOString() : null,
        sendAt: fromLocalDateTimeInputValue(sendAt),
        status: nextStatus || status,
        visibleInArchive,
        contactType: contactsAssociate,
        contactGroup: contactsGroups,
        officeGroup: officeGroups,
        includeBirthdaysAnniversaries,
      };

      if (payload.status === "scheduled") {
        const sendAtDate = payload.sendAt ? new Date(payload.sendAt) : null;
        if (!sendAtDate || Number.isNaN(sendAtDate.getTime())) {
          throw new Error("Send date/time is required to schedule.");
        }
        if (sendAtDate.getTime() <= Date.now()) {
          throw new Error("Send date/time must be in the future.");
        }
      }

      let res;
      if (id) {
        res = await user_service.newslettersUpdate(id, payload);
      } else {
        res = await user_service.newslettersCreate(payload);
      }

      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Saved successfully.",
        message: "Saved",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 1000);

      const saved = res?.data;
      if (nextStatus === "scheduled") {
        navigate("/control-panel/newsletters");
        return;
      }
      if (!id && saved?._id) navigate(`/control-panel/newsletters/edit/${saved._id}`);
      if (saved?.status) setStatus(saved.status);
      setNewsletter(saved);
    } catch (e) {
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: e?.response?.data?.message || e?.message || "Save failed.",
        message: "Error",
      });
    } finally {
      setLoader({ isActive: false });
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const currentStatus = String(newsletter?.status || status);
    if (currentStatus === "sent") return;
    if (!window.confirm("Delete this newsletter? This cannot be undone.")) return;

    setLoader({ isActive: true });
    try {
      await user_service.newslettersDelete(id);
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Newsletter deleted.",
        message: "Deleted",
      });
      setTimeout(() => navigate("/control-panel/newsletters"), 500);
    } catch (e) {
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: e?.response?.data?.message || "Failed to delete newsletter.",
        message: "Error",
      });
    } finally {
      setLoader({ isActive: false });
    }
  };

  const updateVisibility = async (v) => {
    if (!id) return;
    setLoader({ isActive: true });
    try {
      await user_service.newslettersSetVisibility(id, v);
      setVisibleInArchive(v);
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Archive visibility updated.",
        message: "Updated",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 1000);
    } catch (e) {
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: e?.response?.data?.message || "Failed to update visibility.",
        message: "Error",
      });
    } finally {
      setLoader({ isActive: false });
    }
  };

  if (!isAdmin) return null;

  const isSent = String(newsletter?.status || status) === "sent";
  const effectiveReadOnly = readOnly || isSent;

  return (
    <div className="bg-secondary float-left w-100 pt-4">
      <Loader isActive={isActive} />
      {isShow && (
        <Toaster types={types} isShow={isShow} toasterBody={toasterBody} message={message} />
      )}
      <main className="page-wrapper">
        <div className="container pb-5">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h3 className="text-white mb-0">
              {id ? (effectiveReadOnly ? "View Newsletter" : "Edit Newsletter") : "Create Newsletter"}
            </h3>
            <NavLink className="btn btn-secondary" to="/control-panel/newsletters">
              Back to list
            </NavLink>
          </div>

          <div className="bg-light border rounded-3 p-3">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Title</label>
                <input
                  className="form-control"
                  value={title}
                  disabled={effectiveReadOnly}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Publish date</label>
                <input
                  type="date"
                  className="form-control"
                  value={publishDate}
                  disabled={effectiveReadOnly}
                  onChange={(e) => setPublishDate(e.target.value)}
                />
              </div>

              <div className="col-md-5">
                <label className="form-label">Send date & time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={sendAt}
                  disabled={effectiveReadOnly}
                  onChange={(e) => setSendAt(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Status</label>
                <input className="form-control" value={status} disabled />
              </div>

              <div className="col-12">
                <div className="form-check">
                  <input
                    id="include-birthdays-anniversaries"
                    className="form-check-input"
                    type="checkbox"
                    checked={includeBirthdaysAnniversaries}
                    disabled={effectiveReadOnly}
                    onChange={(e) => setIncludeBirthdaysAnniversaries(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="include-birthdays-anniversaries">
                    Include upcoming birthdays &amp; work anniversaries at the bottom of this email
                  </label>
                </div>
                <p className="small text-muted mb-0 ms-4">
                  Adds associates with birthdays or anniversaries in the current week. Leave unchecked to send only your written content.
                </p>
              </div>

              <div className="col-12 border-top pt-3 mt-1">
                <RecipientPicker
                  contactsAssociate={contactsAssociate}
                  setContactsAssociate={setContactsAssociate}
                  contactsGroups={contactsGroups}
                  setContactsGroups={setContactsGroups}
                  officeGroups={officeGroups}
                  setOfficeGroups={setOfficeGroups}
                  disabled={effectiveReadOnly}
                  helperText="Leave empty to send to all active associates when scheduled."
                />
              </div>

              <div className="col-12">
                <label className="form-label">Body</label>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={setEditorState}
                  readOnly={effectiveReadOnly}
                  toolbarHidden={effectiveReadOnly}
                  toolbar={{
                    options: ["inline", "blockType", "fontSize", "list", "textAlign", "history", "link", "image"],
                    inline: { options: ["bold", "italic", "underline", "strikethrough"] },
                    list: { options: ["unordered", "ordered"] },
                    textAlign: { options: ["left", "center", "right"] },
                    history: { options: ["undo", "redo"] },
                    link: { options: ["link", "unlink"] },
                    image: {
                      urlEnabled: true,
                      uploadEnabled: true,
                      alignmentEnabled: true,
                      previewImage: true,
                      inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
                      uploadCallback: (file) =>
                        new Promise((resolve, reject) => {
                          const reader = new FileReader();
                          reader.onload = (e) => resolve({ data: { link: e.target.result } });
                          reader.onerror = (err) => reject(err);
                          reader.readAsDataURL(file);
                        }),
                    },
                  }}
                  wrapperClassName="demo-wrapper"
                  editorClassName="demo-editor"
                  editorStyle={{ height: "400px" }}
                />
              </div>

              <div className="col-12 d-flex flex-wrap gap-2 justify-content-end">
                {isSent ? (
                  <button
                    className={`btn ${visibleInArchive ? "btn-outline-secondary" : "btn-secondary"}`}
                    type="button"
                    onClick={() => updateVisibility(!visibleInArchive)}
                  >
                    {visibleInArchive ? "Hide from archive" : "Show in archive"}
                  </button>
                ) : null}

                {!effectiveReadOnly ? (
                  <>
                    <button className="btn btn-secondary" type="button" onClick={() => save("draft")}>
                      Save draft
                    </button>
                    <button className="btn btn-primary" type="button" onClick={() => save("scheduled")}>
                      Schedule
                    </button>
                  </>
                ) : null}

                {id && !isSent ? (
                  <button className="btn btn-outline-danger" type="button" onClick={handleDelete}>
                    Delete
                  </button>
                ) : null}
                               
                <button className="btn btn-outline-secondary" type="button" onClick={() => navigate("/control-panel")}>
                  Control Panel
                </button>
              </div>
            </div>
          </div>

          {newsletter?.sendErrors?.length ? (
            <div className="bg-light border rounded-3 p-3 mt-3">
              <div className="fw-semibold mb-2">Send errors</div>
              <ul className="mb-0">
                {newsletter.sendErrors.slice(0, 10).map((e, idx) => (
                  <li key={idx} className="small text-muted">
                    {e?.at ? new Date(e.at).toLocaleString() : ""} {e?.message || ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

